import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	PageHeaderAddButton,
	PageHeaderListActions,
	Pagination,
	PaginationHeader,
	ListFilterOptions,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

import { getPageResults, useTerminology } from "utils";
import SiteForm from "./site-form-container";
import SiteListing from "./site-listing";
import { INFO_HOME_ROUTE } from "features/home/home-routes";

const useStyles = makeStyles((theme) => ({
	body: {
		marginTop: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(0),
		},
	},
	filter: { display: "flex" },
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const Sites = ({
	role,
	onAdd,
	onEdit,
	onDelete,
	onSelectSite,
	onGetSitesList,
	pagination,
}) => {
	const classes = useStyles();
	const { sitesLabel, siteLabel, customersLabel } = useTerminology();
	const siteList = [...getPageResults(pagination)].sort((a, b) => {
		const aActive = String(a.status).toLowerCase() === "active";
		const bActive = String(b.status).toLowerCase() === "active";
		return (bActive ? 1 : 0) - (aActive ? 1 : 0);
	});
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const isInitialMount = useRef(true);

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			onGetSitesList({
				page: 1,
				pageSize: 20,
				orderBy: "status,siteName",
				ascending: true,
				searchKey: searchFilter
			});
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter, onGetSitesList]);

	return (
		<Page className={classes.root} title={sitesLabel}>
			<ScrollToTopOnMount />
							<PageHeaderAddButton
					title={sitesLabel}
					addTabTitle="New"
					onAdd={onAdd}
					staffUserHide={false}
					backActionPath={INFO_HOME_ROUTE}
					titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}
				>
					{/* START: Search */}
					{showFilters && (
						<div className={classes.gridContainer}>
							<Grid container spacing={2} className={classes.filterGrid}>
								<Grid item xs={12} md={6}>
									<TextField
										className={classes.search}
										id="input-with-icon-textfield"
										variant="outlined"
										label="Search"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon />
												</InputAdornment>
											),
											endAdornment: searchFilter && (
												<InputAdornment position="end">
													<IconButton
														aria-label="clear search"
								onClick={() => {
																setSearchFilter("");
																onGetSitesList({
																	page: 1,
																	pageSize: 20,
																	orderBy: "status,siteName",
																	ascending: true,
																	searchKey: ""
																});
														}}
														edge="end"
														size="small"
													>
														<ClearIcon />
													</IconButton>
												</InputAdornment>
											)
										}}
										value={searchFilter}
										onChange={(val) => {
											const { value } = val.target;
											setSearchFilter(value);
											// Real-time search with debounce effect will handle the API call
										}}
										style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
										placeholder={`e.g. ${siteLabel} Name, ${customersLabel.replace(/s$/, "")} Name, Address`}
									/>
								</Grid>
							</Grid>
						</div>
					)}
					{/* END: Search */}
				</PageHeaderAddButton>

			<PageContent pad>
				<div className={classes.body}>
					<PaginationHeader hidePagination pagination={pagination} />

					{siteList.map((node, index) => (
						<SiteListing
							key={index}
							site={node}
							role={role}
							onEdit={onEdit}
							onDelete={onDelete}
							onSelectSite={onSelectSite}
						/>
					))}
				</div>

				<Pagination
					hidePagination
					onGetList={onGetSitesList}
					pagination={pagination}
					orderBy="status,siteName"
					searchKey={searchFilter}
				/>
			</PageContent>

			<SiteForm />
		</Page>
	);
};

Sites.propTypes = {
	role: PropTypes.string.isRequired,
	pagination: PropTypes.object.isRequired,
	onAdd: PropTypes.func,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onSelectSite: PropTypes.func.isRequired,
	onGetSitesList: PropTypes.func.isRequired,
};

export default Sites;
